import {Injectable} from '@angular/core';
import {Geometry, Mesh, Vector2, Vector3} from 'three';
import {ViewComponent} from '../view.component';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {ContainerService} from '../../Utils/container.service';
import {RenderService} from '../Scene/render.service';
import {MeshBasicMaterial} from 'three';
import {TextureSizeService} from '../Calculation/texture-size.service';
import {PolySizeService} from '../Calculation/poly-size.service';
import {ObjectUtilsService} from '../object-utils.service';


@Injectable({
    providedIn: 'root'
})
export class ShapeService {

    public surfaceView: ViewComponent;

    private eventCopy: any;
    private _ageStages = 6;

    // private sceneService: SceneService
    constructor(private editorControlService: EditorControlService,
                private containerService: ContainerService,
                private renderService: RenderService,
                private textureSizeService: TextureSizeService,
                private polySizeService: PolySizeService,
                private objectUtils: ObjectUtilsService
    ) {}

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * prepares view to display morphable model
     * prepares morphable model and displays it
     */
    public initializeShapeView() {
        this.eventCopy = this.editorControlService.event;
        this.editorControlService.ctrl.next('pan');

        // // apply texture of original model
        // const textureCtx = this.containerService.textureCanvas.getContext('2d');
        // textureCtx.putImageData(this.containerService.textureCanvas.getContext('2d')
        //                         .getImageData(0, 0, this.containerService.textureRes, this.containerService.textureRes),
        //                         0, 0);
        // this.containerService.texture.needsUpdate = true;

        this.displayMorphableModel((<Geometry>this.containerService.targetModel.geometry).faceVertexUvs[0]);
        this.updatePosition();
        this.renderService.render();
    }

    /**
     * sets the influence of specific target
     * @param value: influence of target
     * @param index: index of target
     * @param update: whether model position should be updated
     */
    public setTargetInfluence(value: number, index: number, update: boolean) {
        // if (Math.abs(this.containerService.targetInfluences[index] - value) > 0.05) {
        //     this.polySizeService.initPolyArea(<Mesh> this.containerService.polyPaintGroup.children[0])
        // }
        this.containerService.targetInfluences[index] = value;
        if (update) {
            this.updatePosition();
        }
    }

    /**
     * Sets the different targets of the model to represent the correct age.
     * Target 0 --> female_adult to 14.5 years
     * Target 1 --> 14.5 to 8.75 years
     * Target 2 --> 8.75 to 5 years
     * Target 3 --> 5 to 3.25 years
     * Target 4 --> 3.25 to 1.75 years
     * Target 5 --> 1.75 to 0.5 years
     * Target 6 --> corpulent slider
     * @param value age of the model.
     */
    public setAgeInfluences(value: number, update) {
        let ageStage = -1;
        let percentage = 0;

        if (value >= 0.5 && value <= 1.75) {
            ageStage = 5;
            percentage = this.calcPercentageInfluence(value, 0.5, 1.75);
        } else if (value >= 1.75 && value <= 3.25) {
            ageStage = 4;
            percentage = this.calcPercentageInfluence(value, 1.75, 3.25);
        } else if (value >= 3.25 && value <= 5) {
            ageStage = 3;
            percentage = this.calcPercentageInfluence(value, 3.25, 5);
        } else if (value >= 5 && value <= 8.75) {
            ageStage = 2;
            percentage = this.calcPercentageInfluence(value, 5, 8.75);
        } else if (value >= 8.75 && value <= 14.5) {
            ageStage = 1;
            percentage = this.calcPercentageInfluence(value, 8.75, 14.5);
        } else if (value >= 14.5 && value <= 18) {
            ageStage = 0;
            percentage = this.calcPercentageInfluence(value, 14.5, 18);
        }

        this.setAgeInfluence(ageStage, percentage, update);
    }

    /**
     * loads model and computes new vertices based on target influences
     * == loads model with applied targets
     */
    public loadMorphedModel() {
        const model = <Mesh>this.containerService.renderGroup.children[0];
        const ccModel = <Mesh>this.containerService.polyPaintGroup.children[0];
        model.frustumCulled = true;

        // move model back to original position
        this.objectUtils.moveTo(model, 0, 0, 0);
        this.objectUtils.moveTo(ccModel, 0, 0, 0);
        this.updateVertices(model, ccModel);


        // TODO @mwimmer if TexeltoFace is needed
        // this.textureSizeService.maptexeltoface(<Mesh>this.containerService.polyPaintGroup.children[0],
        // this.containerService.textureCanvas);

        // update calculation properties
        this.textureSizeService.initArea(model);
        this.textureSizeService.change = true;

        this.renderService.render();
        this.editorControlService.ctrl.next('pan');
    }


    /**
     * calculates new vertex transformation vector
     * @param index
     */
    public getScale(index: number): Vector3 {
        const influence = this.containerService.targetInfluences[index];
        return new Vector3(this.containerService.targetOffsets[index].x * influence,
                           this.containerService.targetOffsets[index].y * influence,
                           this.containerService.targetOffsets[index].z * influence);
    }

    /**
     * render model with given morph target weight
     * @param uvs
     */
    public displayMorphableModel(uvs: Vector2[][]) {
        const targetModel = this.containerService.targetModel;
        (<Geometry>targetModel.geometry).faceVertexUvs[0] = uvs;
        const ccTargetModel = this.containerService.colorCodingTargetModel;
        targetModel.frustumCulled = false;

        // this.loaderService.scaleModel(targetModel);
        // this.loaderService.scaleModel(ccTargetModel);
        this.setMorphTargets(targetModel);
        this.setMorphTargets(ccTargetModel);

        this.containerService.renderGroup.children[0] = targetModel;
        this.containerService.polyPaintGroup.children[0] = ccTargetModel;

        const geometry = <Geometry>targetModel.geometry.clone();
        geometry.morphTargets = [];
        const ccGeometry = <Geometry>ccTargetModel.geometry.clone();
        ccGeometry.morphTargets = [];

        this.containerService.targetModel = new Mesh(geometry, this.containerService.material);
        this.containerService.colorCodingTargetModel = new Mesh(ccGeometry, new MeshBasicMaterial({
            vertexColors: true,
            morphTargets: true
        }));

    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private updateVertices(model, ccModel) {
        (model.geometry as Geometry).morphTargets = [];
        (ccModel.geometry as Geometry).morphTargets = [];
        this.editorControlService.ctrl.next('reset');

        // update vertices to fit target influences
        this.objectUtils.resetTargetInfluences(model, this.containerService.targetInfluences.length);
        this.objectUtils.resetTargetInfluences(ccModel, this.containerService.targetInfluences.length);
        this.objectUtils.updateVertices([model, ccModel],
            this.containerService.targets,
            this.containerService.targetInfluences);
    }

    /**
     * changes position of the model so that it visibly remains still
     */
    private updatePosition() {
        const targetModel = <Mesh>this.containerService.renderGroup.children[0];

        targetModel.position.x = 0;
        targetModel.position.y = 0;
        targetModel.position.z = 0;

        // update position for index 0 - 4 which are the age targets
        for (let i = 0; i < this.containerService.targets.length - 1; ++i) {
            this.updatePositionForIndex(targetModel, i);
        }

        // update position only for shape target
        this.updatePositionForIndex(targetModel, 6, 1);
        this.renderService.render();
    }

    private updatePositionForIndex(targetModel: Mesh, index: number, offset: number = 1) {
        const scale = this.getScale(index);

        // update model position so that morphed model stays in place
        targetModel.position.x -= scale.x / offset;
        targetModel.position.y -= scale.y / offset;
        targetModel.position.z -= scale.z / offset;
    }

    /**
     * apply morph target weight on model
     * @param model
     */
    private setMorphTargets(model: Mesh) {
        (<Geometry>model.geometry).morphTargets = this.containerService.targets;
        // if slider is moved model.morphTargetInfluences is updated in realtime
        model.morphTargetInfluences = this.containerService.targetInfluences;
        // this.loaderService.scaleModel(model);
    }

    /**
     * Calculates how much % the target should be used to represent the correct model.
     * @param value the value of the slider/input.
     * @param lowerAge minimum representable age of target.
     * @param upperAge maximum representable age of target.
     */
    private calcPercentageInfluence(value: number, lowerAge: number, upperAge: number): number {
        const diffAge = upperAge - lowerAge;
        const diffVal = value - lowerAge;

        return 1 - ((diffVal * 100) / (diffAge * 100));
    }

    private setAgeInfluence(ageStage: number, value: number, update: boolean) {
        for (let i = 0; i < this._ageStages; ++i) {
            if (i < ageStage) {
                this.setTargetInfluence(1, i, update);
            } else if (i > ageStage) {
                this.setTargetInfluence(0, i, update);
            } else {
                this.setTargetInfluence(value, i, update);
            }
        }
    }
}
